import { AttestationHistoryFilters } from '@core/configs/tables/dashboard/attestation-history-table.config';

import { useCallback, useMemo, useState } from 'preact/hooks';

const normalizeSearchOption = (searchOption) => {
    const rawValue = searchOption?.value ?? searchOption;

    if (rawValue === 0 || rawValue === '0') {
        return 'all';
    }

    return (rawValue ?? 'all').toString().toLowerCase();
};

export function useHistoryFilters() {
    const [sorting, setSorting] = useState(() => ({
        sortByColumn: AttestationHistoryFilters?.sorting?.sortByColumn ?? 'changeDate',
        sortByDescending: AttestationHistoryFilters?.sorting?.sortByDescending ?? true,
    }));

    const [pagination, setPagination] = useState(() => ({
        pageNumber: AttestationHistoryFilters?.pagination?.paginationData?.pageNumber ?? 1,
        pageSize: AttestationHistoryFilters?.pagination?.paginationData?.pageSize ?? 10,
    }));

    const [search, setSearch] = useState(() => ({
        searchTerm: AttestationHistoryFilters?.search?.searchTerm ?? '',
        searchOption: AttestationHistoryFilters?.search?.searchOption ?? 'all',
    }));

    const [filters, setFilters] = useState(() => ({
        role: AttestationHistoryFilters?.filters?.role ?? '',
        status: AttestationHistoryFilters?.filters?.status,
    }));

    const requestParams = useMemo(() => {
        return {
            pageNumber: pagination.pageNumber,
            pageSize: pagination.pageSize,
            sortByColumn: sorting.sortByColumn,
            sortByDescending: sorting.sortByDescending,
            ...(search.searchTerm ? { searchTerm: search.searchTerm } : {}),
            ...(search.searchOption ? { searchOption: search.searchOption } : {}),
            ...(filters.comments !== null ? { comments: filters.comments } : { comments: null }),
        };
    }, [
        pagination.pageNumber,
        pagination.pageSize,
        sorting.sortByColumn,
        sorting.sortByDescending,
        search.searchTerm,
        search.searchOption,
        filters.active,
    ]);

    const sortingCallback = useCallback((incoming) => {
        setSorting((prev) => ({ ...prev, ...incoming }));
        setPagination((prev) => ({ ...prev, pageNumber: 1 }));
    }, []);

    const paginationCallback = useCallback((incoming) => {
        setPagination((prev) => ({ ...prev, ...incoming }));
    }, []);

    const searchCallback = useCallback((searchTerm, searchOption) => {
        const normalizedSearchOption = normalizeSearchOption(searchOption);

        setSearch((prev) => ({
            ...prev,
            searchTerm,
            searchOption: normalizedSearchOption,
        }));
        setPagination((p) => ({ ...p, pageNumber: 1 }));
    }, []);

    const filtersCallback = useCallback((incoming) => {
        setFilters((prev) => ({
            ...prev,
            ...(incoming ? { comments: incoming?.comments?.value } : { comments: null }),
        }));
        setPagination((p) => ({ ...p, pageNumber: 1 }));
    }, []);

    const tableFilters = useMemo(() => {
        const base = {
            ...AttestationHistoryFilters,
            sorting: {
                ...(AttestationHistoryFilters?.sorting ?? {}),
                ...sorting,
                callback: sortingCallback,
            },
            pagination: {
                ...(AttestationHistoryFilters?.pagination ?? {}),
                paginationData: { ...pagination },
                paginationEvent: paginationCallback,
            },
        };

        if (AttestationHistoryFilters?.advancedFilters) {
            base.advancedFilters = {
                config: {
                    ...(AttestationHistoryFilters.advancedFilters.config || {}),
                },
                callbacks: {
                    search: searchCallback,
                    filter: filtersCallback,
                },
            };
        }

        return base;
    }, [
        searchCallback,
        filtersCallback,
        sortingCallback,
        paginationCallback,
        sorting.sortByColumn,
        sorting.sortByDescending,
        pagination.pageNumber,
        pagination.pageSize,
        filters.active,
    ]);

    return {
        requestParams,
        tableFilters,
    };
}
